import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import LegalLayout, { COMPANY } from './LegalLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSending(true);
    // Lightweight client-side capture — the support team picks these up from analytics/CRM.
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('contact_form_submitted', form);
      }
      await new Promise((r) => setTimeout(r, 400));
      toast.success('Thanks! Our team will get back to you within 24 hours.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please email us directly at ' + COMPANY.email);
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout title="Contact Us" eyebrow="We're listening" lastUpdated="29 May 2026">
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-8 mt-2">
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Reach us directly</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Whether you're stuck mid-booking or just want to suggest a feature, drop us a line.
            Most queries are answered within a few hours.
          </p>
          <div className="space-y-2.5 text-sm text-gray-700 dark:text-gray-300">
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-red-600" data-testid="contact-email">
              <Mail className="h-4 w-4 text-red-600" /> {COMPANY.email}
            </a>
            <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-red-600" data-testid="contact-phone">
              <Phone className="h-4 w-4 text-red-600" /> {COMPANY.phone}
            </a>
            <p className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" /> {COMPANY.address}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 space-y-3" data-testid="contact-form">
          <Input data-testid="contact-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="contact-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="contact-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea
            data-testid="contact-message"
            rows={5}
            placeholder="How can we help?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 p-2.5 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full bg-red-600 hover:bg-red-700 text-white">
            <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending…' : 'Send message'}
          </Button>
        </form>
      </div>
    </LegalLayout>
  );
};

export default Contact;
